"use client";

import { useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { Check, Copy, Eye, EyeOff } from "lucide-react";
import AuthPanel, { useAuthPanelConfig } from "@/components/auth/AuthPanel";
import { Button } from "@/components/ui/Button";
import BrandLogo from "@/components/tenant/BrandLogo";
import { useTenant, useTenantAuthConfig, useDemoMode } from "@/components/tenant/TenantProvider";
import { identify } from "@/lib/tracking/client";

// Kredensial demo publik — SENGAJA di-hardcode & ditampilkan: kartu ini hanya
// render saat tenant ber-demo_mode (bimbelcontoh), dan mode demo menolak semua
// mutasi (middleware demo-guard), jadi tak ada yang bisa dirusak. Password
// di-reset 2026-07-17; bila diganti, hash di DB tenant demo harus ikut diganti.
const DEMO_ACCOUNTS = [
  { role: "Admin", email: "admin@bimbelcontoh.id", password: "DemoAdmin2026!" },
  { role: "Member", email: "siswa.uji.growclass@gmail.com", password: "DemoSiswa2026!" },
] as const;

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={`Salin ${label}`}
      onClick={() => {
        navigator.clipboard?.writeText(value).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          },
          () => {},
        );
      }}
      className="shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-black/[0.04] hover:text-gray-700 transition-colors cursor-pointer"
    >
      {copied ? <Check className="size-3.5 text-[var(--brand-primary)]" /> : <Copy className="size-3.5" />}
    </button>
  );
}

function DemoCredentialsCard() {
  return (
    <div className="mb-6 rounded-xl border border-black/[0.08] bg-white/70 p-4">
      <p className="text-sm font-semibold text-gray-900">Akun demo</p>
      <p className="mt-1 text-xs leading-relaxed text-gray-500">
        Silakan masuk memakai akun demo di bawah. Mode demo bersifat baca-saja — perubahan tidak
        tersimpan.
      </p>

      <div className="mt-3 space-y-2.5">
        {DEMO_ACCOUNTS.map((acc) => (
          <div
            key={acc.email}
            className="rounded-lg border border-black/[0.06] bg-white px-2.5 py-2"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {acc.role}
            </span>
            <div className="mt-1 flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700">
                {acc.email}
              </span>
              <CopyButton value={acc.email} label={`email ${acc.role}`} />
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700">
                {acc.password}
              </span>
              <CopyButton value={acc.password} label={`password ${acc.role}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoginForm() {
  const { brand_name } = useTenant();
  const auth = useTenantAuthConfig();
  const demoMode = useDemoMode();
  // Field panel tidak diteruskan useTenantAuthConfig() → baca window.__TENANT__ langsung.
  const panel = useAuthPanelConfig();
  const hasPanel = panel.style !== "none";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirect");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "";
      const res = await fetch(`${apiUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!data.success) {
        if (data.errorType === 'ERROR_UNVERIFIED') {
          window.location.href = `/verify-email?email=${encodeURIComponent(email)}`;
          return;
        }
        setError(data.error || "Email atau password salah. Coba lagi, atau gunakan “Lupa password” untuk mengatur ulang.");
        return;
      }

      identify();
      const role = data.data.user.role;
      const defaultRedirect = role === "admin" || role === "super_admin" ? "/admin" : "/dashboard";
      const safeRedirect =
        redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//")
          ? redirectTo
          : defaultRedirect;
      window.location.href = safeRedirect;
    } catch {
      setError("Terjadi kesalahan. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main-content" tabIndex={-1} className={hasPanel ? "min-h-screen grid lg:grid-cols-2" : "min-h-screen"}>
      {hasPanel && (
        <AuthPanel
          variant={panel}
          isTyping={isTyping}
          password={password}
          showPassword={showPassword}
        />
      )}

      {/* Form section — style 'none': satu kolom, form tetap center max-w-[420px] */}
      <div
        className={`flex items-center justify-center p-8 bg-[var(--surface)] ${
          hasPanel ? "" : "min-h-screen"
        }`}
      >
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <BrandLogo variant="header" priority />
          </div>

          <div className="text-center mb-10">
            <div className="hidden lg:flex justify-center mb-4">
              <BrandLogo variant="header" priority />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {auth.login_headline ?? `Masuk ke ${brand_name}`}
            </h1>
            <p className="text-sm text-gray-500">
              {auth.login_subtitle ?? "Lanjutkan perjalanan belajarmu"}
            </p>
          </div>

          {demoMode && <DemoCredentialsCard />}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium text-gray-900">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="nama@email.com"
                value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setIsTyping(true)}
                onBlur={() => setIsTyping(false)}
                required
                className="w-full px-3.5 py-3 text-sm rounded-xl border border-black/[0.08] bg-white text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:border-[var(--brand-primary)]/40 focus:ring-2 focus:ring-[var(--brand-primary)]/10 transition-all"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-sm font-medium text-gray-900">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setIsTyping(true)}
                  onBlur={() => setIsTyping(false)}
                  required
                  className="w-full px-3.5 py-3 pr-11 text-sm rounded-xl border border-black/[0.08] bg-white text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:border-[var(--brand-primary)]/40 focus:ring-2 focus:ring-[var(--brand-primary)]/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link
                href="/forgot-password"
                className="text-sm text-[var(--brand-primary)] hover:underline font-medium"
              >
                Lupa password?
              </Link>
            </div>

            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading}
              className="w-full py-3 rounded-xl text-base"
            >
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-8">
            Belum punya akun?{" "}
            <Link href="/register" className="font-semibold text-gray-900 hover:text-[var(--brand-primary)]">
              Daftar sekarang
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <main id="main-content" tabIndex={-1} className="min-h-screen bg-[var(--surface)] flex items-center justify-center">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] p-8">
            <div className="skeleton h-12 w-12 rounded-xl mx-auto mb-4" />
            <div className="skeleton h-6 w-40 mx-auto mb-2" />
            <div className="skeleton h-4 w-56 mx-auto" />
          </div>
        </main>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
